import * as React from "react";

import { ExclamationCircleOutlined } from "@ant-design/icons";
import { AsyncComponent3 } from "@impero-com/common/components/asyncComponent";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import {
    withIdParams,
    withRouter,
} from "@impero-com/common/components/routerUtils";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { RouteFragment } from "@impero-com/common/routeFragments";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import { citextEq } from "@impero-com/common/utils/comparison";
import * as DV from "@impero-com/common/utils/dataView";
import { parseArrayValue } from "@impero-com/common/utils/export";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import {
    FormFrame,
    Divider,
    Button,
    InformationModal,
    PageSpinner,
} from "@impero-com/ui";
import { Popover, Upload, Spin } from "antd";
import { ColumnProps } from "antd/es/table";
import { RcFile } from "antd/es/upload";
import { InfinityTable } from "antd-table-infinity";
import memoizeOne from "memoize-one";
import { NavigateFunction } from "react-router-dom";
import * as XLSX from "xlsx";

import { getEntities } from "api/generated/entities/entities";
import type { UserProfile } from "api/generated/model";
import {
    RiskDirectoryNodeWithDetailsDto as RiskDirectoryNodeDto,
    MetadataInstanceValue,
    EntityOverviewListDto,
    EntityView,
} from "api/generated/model";
import {
    getRisksRiskDirectoryId,
    getRisksRiskDirectoryIdNodes,
    patchRisksRiskDirectoryIdNodes,
} from "api/generated/risks/risks";
import { useLastPresent } from "components/modals/useLastPresent";
import * as R from "org/risks/types";
import { RISK_MODULE } from "org/risks/url";
import { Guid } from "utils/guid";
import { assertNonEmpty } from "utils/nonempty";

import { OrgApplicationBar } from "../../../../components/applicationBar/orgApplicationBar";
import { FormulaComputer } from "../formulaComputer";
import { buildResponseEnumMap, EnumMap } from "../impactLikelihood";
import * as T from "../types";
import * as U from "../utils";

import * as theme from "./theme.css";

// COMPONENTS
class RiskMapImporterWithoutRouter extends React.PureComponent<
    RiskMapImporterProps,
    RiskMapImporterState
> {
    state: RiskMapImporterState = {
        dataStatus: { type: "NO_FILE" },
        status: { type: "DEFAULT" },
        manuallyIgnoredRows: [],
    };
    fileReader: FileReader = new FileReader();

    computeRiskMapImporterTableProps = memoizeOne(
        computeRiskMapImporterTableProps
    );

    render = () => {
        const { id, ajaxManager, navigate } = this.props;
        const { dataStatus, manuallyIgnoredRows, errorDialog } = this.state;
        return (
            <>
                <AsyncComponent3<
                    R.RiskDirectoryView,
                    RiskDirectoryNodeDto,
                    EntityOverviewListDto
                >
                    key={`risk-map-importer-${id}`}
                    promises={[
                        () => getRisksRiskDirectoryId(Number(id)),
                        () => getRisksRiskDirectoryIdNodes(Number(id)),
                        () => getEntities({ all: true, scope: "risk" }),
                    ]}
                    renderLoading={() => <PageSpinner />}
                    renderError={(code) => <LoggedInHTTPError code={code} />}
                    renderContent={(
                        riskDirectoryView,
                        riskDirectoryNode,
                        entities
                    ) => {
                        if (!riskDirectoryView.content.actions.edit) {
                            return <NoAccessCard />;
                        }
                        const tableProps =
                            dataStatus.type === "READ"
                                ? this.computeRiskMapImporterTableProps(
                                      dataStatus.workbook,
                                      riskDirectoryView,
                                      riskDirectoryNode,
                                      entities,
                                      manuallyIgnoredRows
                                  )
                                : undefined;
                        const payload:
                            | Result<
                                  Record<Guid, R.UpdatedRiskDirectoryNode>,
                                  string[]
                              >
                            | undefined =
                            tableProps !== undefined
                                ? computePayload(tableProps)
                                : undefined;
                        return (
                            <OrgApplicationBar
                                title={{
                                    title: t("RiskDirectory_RiskMapImporter"),
                                    breadCrumbs: [
                                        {
                                            label: t(
                                                "RiskDirectory_RiskManager"
                                            ),
                                            url: RISK_MODULE,
                                        },
                                        {
                                            label: riskDirectoryView.content
                                                .resource.title,
                                            url: `${RISK_MODULE}/${id}`,
                                        },
                                    ],
                                }}
                            >
                                <FormFrame>
                                    <div className={theme.uploader}>
                                        <Upload.Dragger
                                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                            className={theme.uploaderDragger}
                                            beforeUpload={(file) => {
                                                this.setState(
                                                    {
                                                        dataStatus: {
                                                            type: "READING",
                                                            file,
                                                        },
                                                    },
                                                    this.readFile
                                                );
                                                return false;
                                            }}
                                            showUploadList={false}
                                        >
                                            <div className={theme.uploaderText}>
                                                {dataStatus.type ===
                                                "NO_FILE" ? (
                                                    t(
                                                        "RiskDirectory_RiskMapImporter_Upload"
                                                    )
                                                ) : (
                                                    <>
                                                        {dataStatus.file.name}
                                                        {dataStatus.type ===
                                                            "READING" && (
                                                            <>
                                                                {"\u00a0"}
                                                                <Spin />
                                                            </>
                                                        )}
                                                        <Button
                                                            id="remove-file"
                                                            className={
                                                                theme.uploaderRemove
                                                            }
                                                            onClick={(
                                                                event
                                                            ) => {
                                                                event.stopPropagation();
                                                                this.setState({
                                                                    dataStatus:
                                                                        {
                                                                            type: "NO_FILE",
                                                                        },
                                                                });
                                                            }}
                                                        >
                                                            {t(
                                                                "RiskDirectory_RiskMapImporter_Remove"
                                                            )}
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </Upload.Dragger>
                                        <Button
                                            id="save"
                                            disabled={tableProps === undefined}
                                            danger={
                                                payload !== undefined &&
                                                payload.type === "ERROR"
                                            }
                                            variant={
                                                payload === undefined
                                                    ? "default"
                                                    : "primary"
                                            }
                                            className={theme.uploaderSave}
                                            loading={
                                                this.state.status.type ===
                                                "SAVING"
                                            }
                                            onClick={() => {
                                                if (payload === undefined) {
                                                    return;
                                                }
                                                if (payload.type === "ERROR") {
                                                    this.setState({
                                                        errorDialog: {
                                                            title: "Some error have been found",
                                                            content: (
                                                                <ErrorList
                                                                    errors={
                                                                        payload.err
                                                                    }
                                                                />
                                                            ),
                                                        },
                                                    });
                                                } else {
                                                    ajaxManager
                                                        .ajax<void, SaveStatus>(
                                                            () =>
                                                                patchRisksRiskDirectoryIdNodes(
                                                                    Number(id),
                                                                    payload.value
                                                                ),
                                                            {
                                                                component: this,
                                                                initialStatusValue:
                                                                    "DEFAULT",
                                                                getErrorStatus:
                                                                    (err) => {
                                                                        this.setState(
                                                                            {
                                                                                errorDialog:
                                                                                    {
                                                                                        title: t(
                                                                                            "Ajax_Error"
                                                                                        ),
                                                                                        content:
                                                                                            err,
                                                                                    },
                                                                            }
                                                                        );
                                                                        return {
                                                                            type: "DEFAULT",
                                                                        };
                                                                    },
                                                                getSuccessStatus:
                                                                    () => {
                                                                        return {
                                                                            type: "DEFAULT",
                                                                        };
                                                                    },
                                                                inFlightStatus:
                                                                    {
                                                                        type: "SAVING",
                                                                    },
                                                            }
                                                        )
                                                        .then(() => {
                                                            navigate(
                                                                `/${RouteFragment.RISKS}/${id}#${T.RISK_MAP_TAB}`
                                                            );
                                                        });
                                                }
                                            }}
                                        >
                                            {t(
                                                "RiskDirectory_RiskMapImporter_Save"
                                            )}
                                        </Button>
                                    </div>
                                    {tableProps !== undefined ? (
                                        <>
                                            <Divider />
                                            <RiskMapImporterTable
                                                header={tableProps.header}
                                                nodes={tableProps.nodes}
                                                refRange={tableProps.refRange}
                                                ignoreRow={this.ignoreRow}
                                            />
                                        </>
                                    ) : null}
                                </FormFrame>
                            </OrgApplicationBar>
                        );
                    }}
                    ajaxManager={ajaxManager}
                />
                <ErrorDialog
                    dialog={errorDialog}
                    onClose={() => this.setState({ errorDialog: undefined })}
                />
            </>
        );
    };

    ignoreRow = (id: string) => {
        const { manuallyIgnoredRows } = this.state;
        const newManuallyIgnoredRows = [...manuallyIgnoredRows, id];
        this.setState({ manuallyIgnoredRows: newManuallyIgnoredRows });
    };

    readFile = () => {
        const { dataStatus } = this.state;
        switch (dataStatus.type) {
            case "READING": {
                if (this.fileReader.readyState === FileReader.LOADING) {
                    this.fileReader.abort();
                }
                this.fileReader.onload = (event) => {
                    if (
                        event.target &&
                        this.state.dataStatus.type === "READING" &&
                        this.state.dataStatus.file.uid === dataStatus.file.uid
                    ) {
                        const data = new Uint8Array(
                            event.target.result as ArrayBuffer
                        );
                        const workbook = XLSX.read(data, { type: "array" });
                        this.setState({
                            dataStatus: {
                                type: "READ",
                                file: dataStatus.file,
                                workbook,
                            },
                        });
                    }
                };
                this.fileReader.readAsArrayBuffer(dataStatus.file);
                break;
            }
            case "NO_FILE":
            case "READ":
                // Nothing to do
                return;
            default:
                assertNever(dataStatus);
        }
    };
}

interface RiskMapImporterProps {
    ajaxManager: AjaxManager;
    orgProfileCookie: UserProfile | null;
    orgCookie: OrgCookie;
    navigate: NavigateFunction;
    id: string;
}

interface ErrorDialogProps {
    dialog: ErrorDialogState | undefined;
    onClose: () => void;
}

function ErrorDialog({ dialog, onClose }: ErrorDialogProps) {
    // Keeps the message in place while the modal animates closed.
    const shown = useLastPresent(dialog);

    return (
        <InformationModal
            open={dialog !== undefined}
            onClose={onClose}
            type="warning"
            title={shown?.title}
            footer={
                <Button variant="primary" onClick={onClose}>
                    {t("Close")}
                </Button>
            }
        >
            {shown?.content}
        </InformationModal>
    );
}

interface ErrorDialogState {
    title: string;
    content: React.ReactNode;
}

interface ErrorListProps {
    errors: Array<string>;
}

function ErrorList({ errors }: ErrorListProps) {
    return (
        <ul>
            {errors.map((error, i) => (
                <li key={i}>{error}</li>
            ))}
        </ul>
    );
}

interface RiskMapImporterState {
    dataStatus: RiskMapImporterStatus;
    status: SaveStatus;
    manuallyIgnoredRows: Array<string>;
    errorDialog?: ErrorDialogState;
}

type RiskMapImporterStatus =
    | { type: "NO_FILE" }
    | { type: "READING"; file: RcFile }
    | { type: "READ"; file: RcFile; workbook: XLSX.WorkBook };

type SaveStatus = { type: "DEFAULT" } | { type: "SAVING" };

class RiskMapImporterTable extends React.PureComponent<RiskMapImporterTableProps> {
    render = () => {
        const { header, nodes, refRange, ignoreRow } = this.props;

        if (header.type === "ERROR") {
            return (
                <div className={theme.headerErrors}>
                    Could not parse header row :
                    <ul>
                        {header.err.map((err, i) => (
                            <li key={i}>{err}</li>
                        ))}
                    </ul>
                </div>
            );
        }

        return (
            <div className={theme.tableContainer}>
                <InfinityTable<TableData>
                    columns={[
                        {
                            key: "INDEX",
                            title: "1",
                            render: (_text, record) => {
                                const offset = 1 + (refRange.s.r ?? 0); // Compute the excel row number: 1 is header, 2 is the first row
                                switch (record.type) {
                                    case "IgnoredRow":
                                        return displayIgnoredRowIndex(
                                            record.row,
                                            offset,
                                            record.ignoreReason
                                        );
                                    case "ValidRow":
                                        return displayRowIndex(record, offset);
                                    case "InvalidRow":
                                        return displayRowErrorIndex(
                                            record.error,
                                            offset
                                        );
                                    default:
                                        return assertNever(record);
                                }
                            },
                            className: theme.indexCell,
                            width: 42,
                        },
                        ...header.value.map(nodeExtractor),
                        {
                            key: "ACTION",
                            title: "Action",
                            render: (_text, record) => {
                                switch (record.type) {
                                    case "IgnoredRow":
                                    case "ValidRow":
                                        return null;
                                    case "InvalidRow":
                                        const error = record.error;
                                        switch (error.type) {
                                            case "AssessedNode":
                                                return (
                                                    <Button
                                                        id={`ignore-row-${record.error.row.line}`}
                                                        onClick={() => {
                                                            ignoreRow(
                                                                error.nodeId
                                                            );
                                                        }}
                                                    >
                                                        Ignore
                                                    </Button>
                                                );
                                            case "GetNode":
                                            case "Metadata":
                                            case "ParseNodeRow":
                                                return null;
                                            default:
                                                return assertNever(error);
                                        }
                                    default:
                                        return assertNever(record);
                                }
                            },
                            className: theme.indexCell,
                        },
                    ]}
                    dataSource={nodes}
                    scroll={{ x: 0, y: 1000 }}
                    pagination={false}
                />
            </div>
        );
    };
}

interface RiskMapImporterTableProps {
    header: Result<HeaderItem[], string[]>;
    nodes: TableData[];
    refRange: XLSX.Range;
    ignoreRow: (nodeId: string) => void;
}

interface RiskMapImporterTableData {
    header: Result<HeaderItem[], string[]>;
    nodes: TableData[];
    refRange: XLSX.Range;
}

// TYPES
/**
 * A cell in the spreadsheet
 */
type Cell =
    | { type: "string"; value: string }
    | { type: "number"; value: number; raw: string };

/**
 * A row in the spreadsheet
 */
interface Row {
    line: number;
    cells: Array<Cell | undefined>;
}

/**
 * Flag for parsed header row items
 */
type HeaderItem =
    | { type: "PROCESS"; index: number }
    | { type: "PROCESS_DESCRIPTION"; index: number }
    | { type: "RISK" }
    | { type: "SCOPE" }
    | { type: "RISK_DESCRIPTION" }
    | { type: "RD_METADATA"; field: R.MetadataField }
    | { type: "RISK_ENTITY" }
    | { type: "ENTITY_METADATA"; field: R.MetadataField };

/**
 * Risk node parsed row
 */
interface NodeRow {
    path: string[];
    name: string;
    scope: "Global" | "Local";
    entity: string;
    description: string | undefined;
    rdMetadata: Array<MetadataValue>;
    entityMetadata: Array<MetadataValue>;
    rawRow: Row;
}

/**
 * Metadata cell parsed value
 */
interface MetadataValue {
    field: R.MetadataField;
    rawCell: Cell | null;
    parsedValue: Result<MetadataInstanceValue, string> | null;
}

/**
 * Content of RiskMapImporterTable
 */
type TableData =
    | {
          type: "ValidRow";
          row: Row;
          node: T.ExpandedRiskNode;
          ancestors: R.ProcessNode[];
          entity: EntityView;
          parsedRow: NodeRow;
          ignoreRDMetadata: boolean;
          readOnlyMetadata: Guid[];
          hiddenMetadata: Guid[];
      }
    | {
          type: "IgnoredRow";
          row: Row;
          ignoreReason: RowIgnoreReason;
      }
    | {
          type: "InvalidRow";
          error: TableError;
      };

type ValidRow = Extract<TableData, { type: "ValidRow" }>;
type RowIgnoreReason = "ManuallyIgnored" | "DuplicatedEntry";

/**
 * Failed to parse a NodeRow
 */
interface ParseNodeRowError {
    type: "ParseNodeRow";
    errors: string[];
    row: Row;
}

/**
 * Failed to find a risk from a NodeRow
 */
interface GetNodeError {
    type: "GetNode";
    error: string;
    row: Row;
}

/**
 * Hidden metadata field set
 */
interface MetadataError {
    type: "Metadata";
    errors: string[];
    row: Row;
}

interface AssessedNodeError {
    type: "AssessedNode";
    row: Row;
    nodeId: string;
    nodeName: string;
}

type TableError =
    | ParseNodeRowError
    | GetNodeError
    | MetadataError
    | AssessedNodeError;

interface Ok<T> {
    type: "OK";
    value: T;
}
interface Err<E> {
    type: "ERROR";
    err: E;
}
type Result<T, E> = Ok<T> | Err<E>;

function mapResult<T, E, U>(
    res: Result<T, E>,
    mapper: (input: T) => U
): Result<U, E> {
    switch (res.type) {
        case "OK":
            return { type: "OK", value: mapper(res.value) };
        case "ERROR":
            return { type: "ERROR", err: res.err };
        default:
            assertNever(res);
    }
}

// Utility functions
/**
 * Parse the first spreadsheet of a workbook
 */
function computeRiskMapImporterTableProps(
    workbook: XLSX.WorkBook,
    riskDirectoryView: R.RiskDirectoryView,
    riskDirectoryNode: RiskDirectoryNodeDto,
    entities: EntityOverviewListDto,
    manuallyIgnoredRows: Array<string>
): RiskMapImporterTableData {
    if (workbook.SheetNames.length > 1) {
        console.warn(
            "Multi-page workbooks aren't supported, only first page will be imported"
        );
    }

    const sheet: XLSX.WorkSheet = workbook.Sheets[workbook.SheetNames[0]];
    const sheetRef = sheet["!ref"];
    if (sheetRef === undefined) {
        throw new Error("sheet ref is undefined");
    }
    const refRange = XLSX.utils.decode_range(sheetRef);

    const headerRowIndex = 0;
    const headerRow = decodeRow(sheet, headerRowIndex, refRange);
    const header = parseHeaderRow(headerRow, riskDirectoryView);

    if (header.type === "ERROR") {
        return {
            header,
            nodes: [],
            refRange,
        };
    }

    const nodeRows: Result<NodeRow, ParseNodeRowError>[] = [];

    // Note that refRange.e.r is about a million in the standard case,
    // so it is not really inclusive contrary to refRange.e.c, see
    // decodeRow.
    for (
        let row = headerRowIndex + 1;
        row < (refRange.e.r ? refRange.e.r + 1 : 0);
        row++
    ) {
        const decodedRow = decodeRow(sheet, row, refRange);
        const nodeRow = parseNodeRow(decodedRow, header.value);
        // This stops at the first blank row.
        // If there is no blank row between the last data row and
        // the Impero footer, an error will be displayed for the
        // Impero footer.
        if (nodeRow === undefined) {
            break;
        }
        nodeRows.push(nodeRow);
    }

    const riskNodeExtractors = buildNodeExtractors(
        riskDirectoryView,
        riskDirectoryNode,
        buildResponseEnumMap(riskDirectoryView)
    );
    const processedNodes: Set<string> = new Set();
    const nodes: TableData[] = nodeRows.reduce((nodes, row) => {
        if (row.type === "ERROR") {
            nodes.push({
                type: "InvalidRow",
                error: row.err,
            });
            return nodes;
        }
        const res = getNode(
            row.value,
            riskDirectoryNode.content.resource.rootNode,
            entities
        );
        if (res.type === "ERROR") {
            nodes.push({
                type: "InvalidRow",
                error: {
                    type: "GetNode",
                    error: res.err,
                    row: row.value.rawRow,
                },
            });
            return nodes;
        }

        const [node, ancestors, entity] = res.value;
        // Ignore the whole row if previously processed (exporter outputs a row per mitigating controls, so that can't be rejected as an error)
        if (processedNodes.has(node.id)) {
            nodes.push({
                type: "IgnoredRow",
                row: row.value.rawRow,
                ignoreReason: "DuplicatedEntry",
            });
            return nodes;
        }

        // Ignore the whole row if it has been manually ignored
        if (manuallyIgnoredRows.find((n) => n === node.id) !== undefined) {
            nodes.push({
                type: "IgnoredRow",
                row: row.value.rawRow,
                ignoreReason: "ManuallyIgnored",
            });
            return nodes;
        }

        if (node.content !== undefined) {
            switch (node.content.assessmentStatus.type) {
                case "Assessed":
                    processedNodes.add(node.id);
                    nodes.push({
                        type: "InvalidRow",
                        error: {
                            type: "AssessedNode",
                            row: row.value.rawRow,
                            nodeId: node.id,
                            nodeName: `${row.value.name} (assigned to Entity ${row.value.entity})`,
                        },
                    });
                    return nodes;
                case undefined:
                case "PreviouslyAssessed":
                case "NeverAssessed":
                    break;
                default:
                    assertNever(node.content.assessmentStatus);
            }
        }

        // Because rd metadata are duplicated, only the first occurrence is persisted. That why we want to validate using these
        const rdMetadataSrc = nodes.find(
            (data) =>
                data.type === "ValidRow" && data.node.risk.id === node.risk.id
        ) as ValidRow | undefined;
        // Ignore rd metadata part of the row, see previous comment
        const ignoreRDMetadata = rdMetadataSrc !== undefined;
        // List of read only metadata ids, allowed to have a value but never persisted
        const readOnlyMetadata: Guid[] = [];
        // List of hidden metadata ids, not allowed to have a value and never persisted
        const hiddenMetadata: Guid[] = [];
        const metadataErrors: string[] = [];

        const rdNode = rdMetadataSrc?.node ?? node;
        const rdRow = rdMetadataSrc?.parsedRow ?? row.value;
        const rdMetadataValues: Record<Guid, MetadataInstanceValue> = {};
        for (const md of rdRow.rdMetadata) {
            if (md.parsedValue !== null && md.parsedValue.type === "OK") {
                rdMetadataValues[md.field.id] = md.parsedValue?.value;
            }
        }
        const entityMetadataValues: Record<Guid, MetadataInstanceValue> = {};
        for (const md of row.value.entityMetadata) {
            if (md.parsedValue !== null && md.parsedValue.type === "OK") {
                entityMetadataValues[md.field.id] = md.parsedValue?.value;
            }
        }
        let scope: R.RiskScope;
        switch (node.risk.content.scope.type) {
            case "LocalWithContent":
                scope = {
                    type: "LocalWithContent",
                    entityId: entity.id,
                    content: {
                        metadataValues: entityMetadataValues,
                        mitigatingControls: [],
                        assessmentStatus: { type: "NeverAssessed" },
                    },
                };
                break;
            case "GlobalWithContent":
                scope = {
                    type: "GlobalWithContent",
                    entityIds: [entity.id],
                    entityIdContent: {
                        [entity.id]: {
                            metadataValues: entityMetadataValues,
                            mitigatingControls: [],
                            assessmentStatus: { type: "NeverAssessed" },
                        },
                    },
                };
                break;
            default:
                assertNever(node.risk.content.scope);
        }
        const value: T.ExpandedRiskNode = {
            type: "ExpandedRiskNode",
            id: node.id,
            entityId: node.entityId,
            // This is a partial view of the node, only using info available in the current row.
            // It's OK because metadata validation only require a view on the current risk-entity
            // But should *never* be used for other purposes nor send to the server
            risk: {
                type: "Risk",
                id: rdNode.risk.id,
                content: {
                    name: rdNode.risk.content.name,
                    description: rdNode.risk.content.description,
                    metadataValues: rdMetadataValues,
                    scope,
                },
            },
            content: {
                metadataValues: entityMetadataValues,
                mitigatingControls: [],
                assessmentStatus: { type: "NeverAssessed" },
            },
        };
        const { hiddenFields } = U.filterAndResolveDirectorySections(
            riskDirectoryView,
            riskNodeExtractors,
            value,
            { riskDirectoryLevel: !ignoreRDMetadata, entityLevel: true }
        );

        readOnlyMetadata.push(
            ...[...hiddenFields.entries()]
                .filter(([_id, status]) => status === "READ_ONLY")
                .map(([id, _status]) => id)
        );
        hiddenMetadata.push(
            ...[...hiddenFields.entries()]
                .filter(([_id, status]) => status === "HIDDEN")
                .map(([id, _status]) => id)
        );

        if (!ignoreRDMetadata) {
            for (const md of row.value.rdMetadata) {
                if (md.parsedValue !== null && md.parsedValue.type === "OK") {
                    if (hiddenFields.get(md.field.id) === "HIDDEN") {
                        const extractor = riskNodeExtractors.find(
                            (extractor) =>
                                extractor.id === md.field.condition!.columnId
                        )!;
                        metadataErrors.push(
                            `Risk directory level column ${md.field.name} is set but not visible due to ${extractor.label} value`
                        );
                    }
                }
            }
        }
        for (const md of row.value.entityMetadata) {
            if (md.parsedValue !== null && md.parsedValue.type === "OK") {
                if (hiddenFields.get(md.field.id) === "HIDDEN") {
                    const extractor = riskNodeExtractors.find(
                        (extractor) =>
                            extractor.id === md.field.condition!.columnId
                    )!;
                    metadataErrors.push(
                        `Entity level column ${md.field.name} is set but not visible due to ${extractor.label} value`
                    );
                }
            }
        }

        if (metadataErrors.length > 0) {
            nodes.push({
                type: "InvalidRow",
                error: {
                    type: "Metadata",
                    errors: metadataErrors,
                    row: row.value.rawRow,
                },
            });
            return nodes;
        } else {
            processedNodes.add(node.id);
            nodes.push({
                type: "ValidRow",
                node,
                row: row.value.rawRow,
                parsedRow: row.value,
                ancestors,
                entity,
                ignoreRDMetadata,
                readOnlyMetadata,
                hiddenMetadata,
            });
            return nodes;
        }
    }, [] as Array<TableData>);

    return {
        header,
        nodes,
        refRange,
    };
}

/**
 * Compute the Ajax payload, or collect all the errors in table
 */
function computePayload(
    tableProps: RiskMapImporterTableData
): Result<Record<Guid, R.UpdatedRiskDirectoryNode>, string[]> {
    const { header, nodes } = tableProps;
    if (header.type === "ERROR") {
        return err(header.err);
    }
    const payload: Record<Guid, R.UpdatedRiskDirectoryNode> = {};
    const insertDefault = (risk: R.RiskNode) => {
        const id = risk.id;
        payload[id] = {};
    };
    const errors: string[] = [];
    for (const node of nodes) {
        switch (node.type) {
            case "IgnoredRow":
                break;
            case "InvalidRow":
                switch (node.error.type) {
                    case "ParseNodeRow":
                        errors.push(
                            `Failed to parse node row: ${node.error.errors.join(
                                ", "
                            )}`
                        );
                        break;
                    case "GetNode":
                        errors.push(
                            `Failed to find risk node: ${node.error.error}`
                        );
                        break;
                    case "Metadata":
                        errors.push(
                            `Failed to validate metadata: ${node.error.errors.join(
                                ", "
                            )}`
                        );
                        break;
                    case "AssessedNode":
                        errors.push(
                            `The risk ${node.error.nodeName} is assessed and can not be modified`
                        );
                        break;
                    default:
                        assertNever(node.error);
                }
                break;
            case "ValidRow":
                const risk = node.node.risk;
                const id = risk.id;
                if (!(id in payload)) {
                    insertDefault(risk);
                }
                const nodePayload = payload[id];
                if (!node.ignoreRDMetadata) {
                    nodePayload.description = node.parsedRow.description ?? "";
                    nodePayload.metadataValues = {};
                    for (const rdMetadata of node.parsedRow.rdMetadata.filter(
                        (md) =>
                            !shouldIgnoreFieldType(md.field.fieldType) &&
                            !U.hasFormula(md.field.fieldType) &&
                            !node.readOnlyMetadata.includes(md.field.id) &&
                            !node.hiddenMetadata.includes(md.field.id)
                    )) {
                        if (rdMetadata.parsedValue !== null) {
                            if (rdMetadata.parsedValue.type === "ERROR") {
                                errors.push(
                                    `Metadata error in ${node.parsedRow.name}: ${rdMetadata.parsedValue.err}`
                                );
                            } else {
                                nodePayload.metadataValues[
                                    rdMetadata.field.id
                                ] = rdMetadata.parsedValue.value;
                            }
                        } else {
                            nodePayload.metadataValues[rdMetadata.field.id] =
                                null;
                        }
                    }
                }
                if (nodePayload.scope === undefined) {
                    nodePayload.scope = {
                        type:
                            node.parsedRow.scope === "Local"
                                ? "UpdatedLocalWithContent"
                                : "UpdatedGlobalWithContent",
                    };
                }
                const nodeScope = nodePayload.scope as R.UpdatedRiskScope;
                for (const entityMetadata of node.parsedRow.entityMetadata.filter(
                    (md) =>
                        !shouldIgnoreFieldType(md.field.fieldType) &&
                        !U.hasFormula(md.field.fieldType) &&
                        !node.readOnlyMetadata.includes(md.field.id) &&
                        !node.hiddenMetadata.includes(md.field.id)
                )) {
                    if (
                        entityMetadata.parsedValue !== null &&
                        entityMetadata.parsedValue.type === "ERROR"
                    ) {
                        errors.push(
                            `Metadata error in ${node.parsedRow.name}: ${entityMetadata.parsedValue.err}`
                        );
                    } else {
                        switch (nodeScope.type) {
                            case "UpdatedGlobalWithContent":
                                if (nodeScope.entityIdContent === undefined) {
                                    nodeScope.entityIdContent = {};
                                }
                                if (
                                    nodeScope.entityIdContent[
                                        node.entity.id
                                    ] === undefined
                                ) {
                                    nodeScope.entityIdContent[node.entity.id] =
                                        {
                                            metadataValues: {},
                                        };
                                }
                                nodeScope.entityIdContent[
                                    node.entity.id
                                ].metadataValues![entityMetadata.field.id] =
                                    entityMetadata.parsedValue?.value ?? null;
                                break;
                            case "UpdatedLocalWithContent":
                                if (nodeScope.content === undefined) {
                                    nodeScope.content = { metadataValues: {} };
                                }
                                nodeScope.content.metadataValues![
                                    entityMetadata.field.id
                                ] = entityMetadata.parsedValue?.value ?? null;
                                break;
                            default:
                                assertNever(nodeScope);
                        }
                    }
                }
                break;
            default:
                assertNever(node);
        }
    }

    if (errors.length === 0) {
        return ok(payload);
    } else {
        return err(errors);
    }
}

function isCell(
    entry: XLSX.CellObject | XLSX.Range | string | undefined
): entry is XLSX.CellObject {
    return entry !== undefined && (entry as XLSX.CellObject).v !== undefined;
}

function decodeRow(
    worksheet: XLSX.WorkSheet,
    rowIndex: number,
    refRange: XLSX.Range
): Row {
    const cells: Array<Cell | undefined> = [];
    // Note that refRange.e.c is inclusive
    for (
        let col = refRange.s.c ?? 0;
        col < (refRange.e.c ? refRange.e.c + 1 : 0);
        col++
    ) {
        const entry: XLSX.CellObject | XLSX.Range | string | undefined =
            worksheet[
                XLSX.utils.encode_cell({
                    c: col,
                    r: rowIndex + (refRange.s.r ?? 0),
                })
            ];
        if (isCell(entry)) {
            switch (typeof entry.v) {
                case "string":
                    cells.push({ type: "string", value: entry.v });
                    break;
                case "number":
                    cells.push({
                        type: "number",
                        value: entry.v,
                        raw: entry.w!,
                    });
                    break;
                default:
                    cells.push(undefined);
            }
        } else {
            cells.push(undefined);
        }
    }
    return { line: rowIndex, cells };
}

function parseHeaderRow(
    row: Row,
    riskDirectoryView: R.RiskDirectoryView
): Result<HeaderItem[], string[]> {
    const header: HeaderItem[] = [];
    const errors: string[] = [];
    let i = 0;

    // First parse processes
    while (i < row.cells.length) {
        const cell = row.cells[i];
        if (cell?.type !== "string") {
            break;
        }
        const regResult = /^Process(\d+)$/.exec(cell.value);
        if (regResult === null) {
            break;
        }
        const index = Number(regResult[1]) - 1;
        header.push({ type: "PROCESS", index });
        i += 1;

        // Optional support for process description column after each process column
        const nextCell = row.cells[i];
        if (
            nextCell?.type === "string" &&
            /^Process\d+ Description$/.exec(nextCell.value) !== null
        ) {
            header.push({ type: "PROCESS_DESCRIPTION", index });
            i += 1;
        }
    }

    // Then we should have the risk column
    if (row.cells[i]?.value !== "Risk") {
        errors.push(`Expected "Risk" got "${row.cells[i]?.value}"`);
    }
    header.push({ type: "RISK" });
    i += 1;

    // The scope column
    if (row.cells[i]?.value !== "Scope") {
        errors.push(`Expected "Scope" got "${row.cells[i]?.value}"`);
    }
    header.push({ type: "SCOPE" });
    i += 1;

    // The risk description column
    if (row.cells[i]?.value !== "Risk Description") {
        errors.push(`Expected "Risk Description" got "${row.cells[i]?.value}"`);
    }
    header.push({ type: "RISK_DESCRIPTION" });
    i += 1;

    const {
        content: {
            resource: { structure },
        },
    } = riskDirectoryView;

    // Risk directory level metadata columns
    const rdLevelFields =
        structure.nodeTypeToDirectoryLevelFields.Risk?.filter(
            (field) =>
                field.fieldType.type !== "Heading" &&
                structure.sections.some((sec) =>
                    sec.sectionColumns.some((col) => col.includes(field.id))
                )
        ) ?? [];
    let lastIndex = i + rdLevelFields.length;
    while (i < lastIndex) {
        const cell = row.cells[i];
        if (cell === undefined) {
            errors.push("Expected risk directory level metadata columns name");
        } else {
            const title = cellToString(cell);
            if (title.type === "OK") {
                const field = rdLevelFields.find(
                    (field) => field.name === title.value
                );
                if (field === undefined) {
                    errors.push(
                        `Cannot find risk directory level metadata column "${title.value}"`
                    );
                } else {
                    header.push({ type: "RD_METADATA", field });
                }
            } else {
                errors.push(title.err);
            }
        }
        i += 1;
    }

    // The risk description column
    if (row.cells[i]?.value !== "Entity") {
        errors.push(`Expected "Entity" got "${JSON.stringify(row.cells[i])}"`);
    }
    header.push({ type: "RISK_ENTITY" });
    i += 1;

    // Entity level metadata columns
    const entityLevelFields = structure.entityLevelFields.filter(
        (field) =>
            field.fieldType.type !== "Heading" &&
            structure.sections.some((sec) =>
                sec.sectionColumns.some((col) => col.includes(field.id))
            )
    );
    lastIndex = i + entityLevelFields.length;
    while (i < lastIndex) {
        const cell = row.cells[i];
        if (cell === undefined) {
            errors.push("Expected entity level metadata columns name");
        } else {
            const title = cellToString(cell);
            if (title.type === "OK") {
                const field = entityLevelFields.find(
                    (field) => field.name === cell.value
                );
                if (field === undefined) {
                    errors.push(
                        `Cannot find entity level metadata column "${cell.value}"`
                    );
                } else {
                    header.push({ type: "ENTITY_METADATA", field });
                }
            } else {
                errors.push(title.err);
            }
        }
        i += 1;
    }

    // ignore remaining entries (related to control program)

    return errors.length === 0 ? ok(header) : err(errors);
}

function parseNodeRow(
    row: Row,
    headerRow: HeaderItem[]
): Result<NodeRow, ParseNodeRowError> | undefined {
    const errors: string[] = [];
    const path: string[] = [];
    let name: string | undefined;
    let scope: "Global" | "Local" | undefined;
    let entity: string | undefined;
    let description: string | undefined;
    const rdMetadata: Array<MetadataValue> = [];
    const entityMetadata: Array<MetadataValue> = [];
    let i = 0;
    for (const header of headerRow) {
        const cell = row.cells[i];
        switch (header.type) {
            case "PROCESS":
                if (isDefinedCell(cell)) {
                    const str = cellToString(cell);
                    if (str.type === "OK") {
                        path.push(str.value);
                    } else {
                        errors.push(str.err);
                    }
                }
                break;
            case "RISK":
                if (isDefinedCell(cell)) {
                    const str = cellToString(cell);
                    if (str.type === "OK") {
                        name = str.value;
                    } else {
                        errors.push(str.err);
                    }
                }
                break;
            case "SCOPE":
                if (cell?.value === "Global") {
                    scope = "Global";
                } else if (cell?.value === "Local") {
                    scope = "Local";
                }
                break;
            case "RISK_ENTITY":
                if (isDefinedCell(cell)) {
                    const str = cellToString(cell);
                    if (str.type === "OK") {
                        entity = str.value;
                    } else {
                        errors.push(str.err);
                    }
                }
                break;
            case "RISK_DESCRIPTION":
                if (isDefinedCell(cell)) {
                    const str = cellToString(cell);
                    if (str.type === "OK") {
                        description = str.value;
                    } else {
                        errors.push(str.err);
                    }
                }
                break;
            case "RD_METADATA":
                if (isDefinedCell(cell)) {
                    rdMetadata.push({
                        field: header.field,
                        rawCell: cell,
                        parsedValue: parseMetadataValue(header.field, cell),
                    });
                } else {
                    rdMetadata.push({
                        field: header.field,
                        rawCell: null,
                        parsedValue: null,
                    });
                }
                break;
            case "ENTITY_METADATA":
                if (isDefinedCell(cell)) {
                    entityMetadata.push({
                        field: header.field,
                        rawCell: cell,
                        parsedValue: parseMetadataValue(header.field, cell),
                    });
                } else {
                    entityMetadata.push({
                        field: header.field,
                        rawCell: null,
                        parsedValue: null,
                    });
                }
                break;
            case "PROCESS_DESCRIPTION":
                break;
            default:
                assertNever(header);
        }
        i += 1;
    }

    if (name === undefined || scope === undefined || entity === undefined) {
        if (name === undefined) {
            if (path.length === 0) {
                // That's the last line parsed
                return undefined;
            }
            errors.push("Name is undefined");
        }
        if (scope === undefined) {
            errors.push("Scope is undefined");
        }
        if (entity === undefined) {
            errors.push("Entity is undefined");
        }
        return err({ type: "ParseNodeRow", errors, row });
    } else {
        return errors.length === 0
            ? ok({
                  description,
                  entity,
                  entityMetadata,
                  name,
                  path,
                  rdMetadata,
                  scope,
                  rawRow: row,
              })
            : err({ type: "ParseNodeRow", errors, row });
    }
}

function isDefinedCell(cell: Cell | undefined): cell is Cell {
    return cell !== undefined && !(cell.type === "string" && cell.value === "");
}

function getNode(
    row: NodeRow,
    rdNode: R.RiskDirectoryNode,
    entities: EntityOverviewListDto
): Result<[T.ExpandedRiskNode, R.ProcessNode[], EntityView], string> {
    const entity = entities.content.find((entity) =>
        citextEq(entity.resource.name, row.entity)
    );
    if (entity === undefined) {
        return err(`Could not find entity ${row.entity}`);
    }
    const entityId = entity.resource.id;
    let currentNode: R.ParentNode = rdNode;
    const ancestors: R.ProcessNode[] = [];
    for (const process of row.path) {
        const nextNode: R.StandardNode | undefined = currentNode.nodes.find(
            (child) =>
                child.type === "Process" &&
                citextEq(child.content.name, process) &&
                (child.content.scope.type === "Global" ||
                    child.content.scope.entityId === entityId)
        );
        if (nextNode === undefined) {
            return err(
                `Could not find process ${process} in ${currentNode.content.name}`
            );
        }
        if (nextNode.type === "Risk") {
            return err("Expected process node, got a risk node");
        }
        ancestors.push(nextNode);
        currentNode = nextNode;
    }

    const risk = currentNode.nodes.find(
        (child) =>
            child.type === "Risk" &&
            citextEq(child.content.name, row.name) &&
            (child.content.scope.type === "GlobalWithContent" ||
                child.content.scope.entityId === entityId)
    );
    if (risk === undefined) {
        return err(`Could not find risk ${row.name}`);
    }
    if (risk.type === "Process") {
        return err("Expected risk node, got a process node");
    }

    return ok([
        {
            type: "ExpandedRiskNode",
            id: `${risk.id}_${entityId}`, // doesn't really matter in the importer, must just be unique
            entityId,
            risk,
            content:
                risk.content.scope.type === "GlobalWithContent"
                    ? risk.content.scope.entityIdContent[entityId]
                    : risk.content.scope.content,
        },
        ancestors,
        {
            ...entity.resource,
            description: entity.resource.description ?? null,
            responsibleId: entity.resource.responsibleId ?? null,
        },
    ]);
}

function nodeExtractor(
    header: HeaderItem,
    colIndex: number
): ColumnProps<TableData> {
    let name: string;
    let label: string;
    let getData: (data: ValidRow) => React.ReactNode;
    let cellClassName: string | undefined;

    switch (header.type) {
        // Identifiers are taken from server
        case "PROCESS":
            name = `Process${header.index}`;
            label = `Process${header.index + 1}`;
            getData = (data) => data.ancestors[header.index]?.content?.name;
            break;
        case "PROCESS_DESCRIPTION":
            name = `Process${header.index} Description`;
            label = `Process${header.index + 1} Description`;
            getData = (data) => (
                <span className={theme.cellIgnore}>
                    {data.ancestors[header.index]?.content?.description}
                </span>
            );
            break;
        case "RISK":
            name = "Risk";
            label = "Risk";
            getData = (data) => data.node.risk.content.name;
            break;
        case "SCOPE":
            name = "Scope";
            label = "Scope";
            getData = (data) =>
                data.node.risk.content.scope.type === "GlobalWithContent"
                    ? "Global"
                    : "Local";
            break;
        case "RISK_ENTITY":
            name = "Entity";
            label = "Entity";
            getData = (data) => data.entity.name;
            break;
        // Editable fields are taken from row
        case "RISK_DESCRIPTION":
            name = "Description";
            label = "Description";
            getData = (data) => (
                <span
                    className={
                        data.ignoreRDMetadata ? theme.cellIgnore : undefined
                    }
                >
                    {data.parsedRow.description}
                </span>
            );
            break;
        case "RD_METADATA":
            name = header.field.id;
            label = header.field.name;
            getData = (data) => {
                const result = data.parsedRow.rdMetadata.find(
                    (value) => value?.field.id === header.field.id
                )!;
                const ignore =
                    shouldIgnoreFieldType(header.field.fieldType) ||
                    data.ignoreRDMetadata ||
                    data.readOnlyMetadata.includes(header.field.id);
                if (result.parsedValue === null) {
                    return undefined;
                }
                return result.parsedValue.type === "OK" ? (
                    <span className={ignore ? theme.cellIgnore : undefined}>
                        {result.rawCell?.value}
                    </span>
                ) : (
                    <span className={theme.cellError}>
                        {result.parsedValue.err}
                    </span>
                );
            };
            if (U.hasFormula(header.field.fieldType)) {
                cellClassName = theme.cellIgnore;
            }
            break;
        case "ENTITY_METADATA":
            name = header.field.id;
            label = header.field.name;
            getData = (data) => {
                const result = data.parsedRow.entityMetadata.find(
                    (value) => value?.field.id === header.field.id
                )!;
                const ignore =
                    shouldIgnoreFieldType(header.field.fieldType) ||
                    data.readOnlyMetadata.includes(header.field.id);
                if (result.parsedValue === null) {
                    return undefined;
                }
                return result.parsedValue.type === "OK" ? (
                    <span className={ignore ? theme.cellIgnore : undefined}>
                        {result.rawCell?.value}
                    </span>
                ) : (
                    <span className={theme.cellError}>
                        {result.parsedValue.err}
                    </span>
                );
            };
            if (U.hasFormula(header.field.fieldType)) {
                cellClassName = theme.cellIgnore;
            }
            break;
        default:
            assertNever(header);
    }

    const renderFn = (_text: unknown, record: TableData) => {
        switch (record.type) {
            case "IgnoredRow":
                return (
                    <span className={theme.cellIgnore}>
                        {record.row.cells[colIndex]?.value}
                    </span>
                );
            case "ValidRow":
                return <span>{getData(record)}</span>;
            case "InvalidRow":
                return displayRowError(record.error, colIndex);
            default:
                return assertNever(record);
        }
    };

    return {
        key: name,
        title: label,
        render: renderFn,
        className: cellClassName,
    };
}

function shouldIgnoreFieldType(fieldType: R.FieldType): boolean {
    switch (fieldType.type) {
        case "Heading":
            // Note: Theoretically this should not happen as those fields are
            // ignored before this is called
            return true;
        case "AttachedFiles":
            // For now, attaching Files can only be done using the FileUploader.
            return true;
        case "TextArea":
        case "TextInput":
        case "NumericInput":
        case "Dropdown":
        case "CheckboxGroup":
        case "RadioButtonGroup":
            return false;
        default:
            return assertNever(fieldType);
    }
}

function parseMetadataValue(
    field: R.MetadataField,
    cell: Cell
): Result<MetadataInstanceValue, string> {
    switch (field.fieldType.type) {
        case "TextArea":
        case "TextInput":
            const valueText = cellToString(cell);
            return mapResult(valueText, (value) => {
                return { type: "Text", value };
            });
        case "NumericInput":
            const valueNumber = cellToNumber(cell);
            return mapResult(valueNumber, (value) => {
                return { type: "Number", value };
            });
        case "Dropdown":
        case "RadioButtonGroup": {
            const str = cellToString(cell);
            if (str.type === "OK") {
                const option = field.fieldType.options.find((option) =>
                    citextEq(option.value, str.value)
                );
                if (option === undefined) {
                    return err(
                        `Cannot find option "${
                            str.value
                        }".\n Available options : ${field.fieldType.options
                            .map((option) => `"${option.value}"`)
                            .join(", ")}`
                    );
                }
                return ok({ type: "OptionValueId", value: option.id });
            } else {
                return str;
            }
        }
        case "CheckboxGroup": {
            const selectedIds: Guid[] = [];
            const errs: string[] = [];
            const str = cellToString(cell);
            if (str.type === "OK") {
                for (const val of parseArrayValue(str.value)) {
                    const option = field.fieldType.options.find((option) =>
                        citextEq(option.value, val)
                    );
                    if (option !== undefined) {
                        selectedIds.push(option.id);
                    } else {
                        errs.push(`Cannot find option "${val}".`);
                    }
                }
                if (errs.length !== 0) {
                    return err(
                        errs.join("\n") +
                            `\n Available options : ${field.fieldType.options
                                .map((option) => `"${option.value}"`)
                                .join(", ")}`
                    );
                }
                if (selectedIds.length === 0) {
                    return err("Array of selected values can not be empty");
                }
                return ok({
                    type: "OptionValueIds",
                    value: assertNonEmpty(selectedIds),
                });
            } else {
                return str;
            }
        }
        case "Heading":
            throw new Error("Cannot parse heading values");
        case "AttachedFiles":
            const str = cellToString(cell);
            if (str.type !== "OK") {
                return str;
            }
            const parsedValues = parseArrayValue(str.value);
            if (parsedValues.length === 0) {
                return err("Attached files can not be empty");
            }
            return ok({ type: "BlobIds", value: assertNonEmpty(parsedValues) });
        default:
            assertNever(field.fieldType);
    }
}

function displayRowError(err: TableError, colIndex: number): React.ReactNode {
    return (
        <span className={theme.cellError}>
            {err.row.cells[colIndex]?.value}
        </span>
    );
}

function displayIgnoredRowIndex(
    row: Row,
    offset: number,
    reason: RowIgnoreReason
): React.ReactNode {
    return (
        <span>
            {row.line + offset}
            {
                <Popover
                    placement="bottomLeft"
                    trigger="click"
                    title={ignoredRowTitle(reason)}
                    getTooltipContainer={() =>
                        document.getElementById("import-risk-map") ??
                        (undefined as any)
                    }
                    content="This row will be ignored when persisting"
                >
                    <ExclamationCircleOutlined />
                </Popover>
            }
        </span>
    );
}

function ignoredRowTitle(reason: RowIgnoreReason): string {
    switch (reason) {
        case "DuplicatedEntry":
            return "Duplicated row";
        case "ManuallyIgnored":
            return "Manually ignored row";
        default:
            return assertNever(reason);
    }
}

function displayRowIndex(value: ValidRow, offset: number): React.ReactNode {
    return <span>{value.row.line + offset}</span>;
}

function displayRowErrorIndex(
    err: TableError,
    offset: number
): React.ReactNode {
    return (
        <Popover
            placement="bottomLeft"
            trigger="click"
            title={errorTitle(err)}
            getTooltipContainer={() =>
                document.getElementById("import-risk-map") ?? (undefined as any)
            }
            content={popupContentRowErrorIndex(err)}
        >
            <div className={theme.cellError}>{err.row.line + offset}</div>
        </Popover>
    );
}

function popupContentRowErrorIndex(err: TableError) {
    switch (err.type) {
        case "GetNode":
            return <p>{err.error}</p>;
        case "AssessedNode":
            return (
                <p>{`The risk ${err.nodeName} is assessed and can not be modified`}</p>
            );
        case "ParseNodeRow":
        case "Metadata":
            return (
                <ul>
                    {err.errors.map((e, i) => (
                        <li key={i}>{e}</li>
                    ))}
                </ul>
            );
    }
}

function errorTitle(err: TableError): string {
    switch (err.type) {
        case "GetNode":
            return "Could not find node";
        case "ParseNodeRow":
            return "Could not parse row";
        case "Metadata":
            return "Hidden metadata field set";
        case "AssessedNode":
            return "Assessed node";
        default:
            assertNever(err);
    }
}

function buildNodeExtractors(
    riskDirectory: R.RiskDirectoryView,
    rootNode: RiskDirectoryNodeDto,
    responseEnumMap: EnumMap<R.ResponseEnum>
): DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>[] {
    const formulaComputer = new FormulaComputer();
    const fields = U.getRiskDirectoryLevelMDFields(
        riskDirectory,
        "Risk"
    ).concat(U.getEntityLevelMDFields(riskDirectory));
    return DV.buildDataExtractors(
        fields.map((field) =>
            U.buildNodeMetadataExtractorSpec(
                field,
                riskDirectory,
                rootNode.linkedResources.blobs,
                responseEnumMap,
                formulaComputer
            )
        )
    );
}

function cellToString(cell: Cell): Result<string, string> {
    switch (cell.type) {
        case "string":
            return ok(cell.value);
        case "number": {
            if (cell.value.toString() !== cell.raw) {
                // Dates & monetary cells are identified as numbers but their parsed value differs from raw representation.
                // That is most likely not what the user expect so we flag them as an error
                return err(
                    `Invalid numeric representation: got '${cell.value}', expected '${cell.raw}'. Prefix the cell content with a quote to force text representation`
                );
            } else if (!Number.isInteger(cell.value)) {
                // Non-integers numbers doesn't preserve the original local (comma versus dot as decimal separator), so we flag them as an error
                return err(
                    `Decimal representation of '${cell.value}' can vary depending of locals, prefix the cell content with a quote to force text representation`
                );
            } else {
                return ok(cell.value.toString());
            }
        }
        default:
            assertNever(cell);
    }
}

function cellToNumber(cell: Cell): Result<number, string> {
    switch (cell.type) {
        case "number": {
            if (cell.value.toString() !== cell.raw) {
                // Dates & monetary cells are identified as numbers but their parsed value differs from raw representation.
                // That is most likely not what the user expect so we flag them as an error
                return err(
                    `Invalid numeric representation: got '${cell.value}', expected '${cell.raw}'. Prefix the cell content with a quote to force text representation`
                );
            } else {
                return ok(cell.value);
            }
        }
        case "string": {
            const num = Number(cell.value);
            if (Number.isNaN(num)) {
                return err(
                    `Invalid text representation of a number : '${cell.value}', remove the quote prefix in order to force number representation`
                );
            }
            return ok(num);
        }
        default:
            assertNever(cell);
    }
}

function ok<T>(value: T): Result<T, never> {
    return { type: "OK", value };
}

function err<E>(err: E): Result<never, E> {
    return { type: "ERROR", err };
}

export const RiskMapImporter = withRouter(
    withIdParams(RiskMapImporterWithoutRouter)
);
